import { DIRS } from './constants'
import { unlinkVehicles } from './coupling'
import { rebuildTopology } from './graph'
import { parsePort } from './keys'
import {
  findConnectionChain,
  getEdge,
  getVehicleById,
  isCellOccupied,
  isLocomotiveMoving,
} from './queries'
import type { Network, Vehicle } from './types'

const approach = (current: number, target: number, maxDelta: number): number => {
  const diff = target - current
  return Math.abs(diff) <= maxDelta ? target : current + Math.sign(diff) * maxDelta
}

export function applyInputPhysics(
  network: Network,
  dt: number,
  forward: boolean,
  backward: boolean,
) {
  const { locomotive: loco } = network
  if (!loco || !(dt > 0)) return
  if (!Number.isFinite(loco.currentVelocity)) loco.currentVelocity = 0

  const isBlockedForward = loco.uncoupleDirectionLock === 'forward'
  const isBlockedBackward = loco.uncoupleDirectionLock === 'backward'
  if (isLocomotiveMoving(loco)) {
    const movingAwayFromLock =
      (isBlockedForward && loco.currentVelocity < 0) ||
      (isBlockedBackward && loco.currentVelocity > 0)
    if (movingAwayFromLock) loco.uncoupleDirectionLock = undefined
  }

  if (forward && isBlockedForward) return
  if (backward && isBlockedBackward) return

  if (forward && network.bumpBlocked === 'forward') return
  if (backward && network.bumpBlocked === 'backward') return

  const wasMoving = isLocomotiveMoving(loco)
  const targetVelocity = (forward ? loco.speed : 0) - (backward ? loco.speed : 0)

  if (loco.currentVelocity !== targetVelocity) {
    const isBraking =
      targetVelocity === 0 || Math.sign(targetVelocity) !== Math.sign(loco.currentVelocity)
    const accelRate = isBraking ? loco.deceleration : loco.acceleration
    loco.currentVelocity = approach(loco.currentVelocity, targetVelocity, accelRate * dt)
  }

  const isStopped = !isLocomotiveMoving(loco)
  if (wasMoving && isStopped) {
    computeUncoupleStatus(network)
  }
}

export function computeUncoupleStatus(network: Network) {
  const { locomotive: loco } = network
  if (!loco) return

  const connected = findConnectionChain(network, loco.id)

  for (const [wagonId, wagon] of network.wagons) {
    wagon.canUncouple = connected.has(wagonId)
      ? isUncoupleAllowed(network, wagonId, connected)
      : undefined
  }
}

/** Flips a switch to its next shape. Refused while any vehicle sits on the cell. */
export function toggleSwitch(network: Network, x: number, y: number): boolean {
  const cell = network.grid[y]?.[x]

  if (cell?.type !== 'switch' || !cell.shapes?.length || cell.active === undefined) {
    return false
  }
  if (isCellOccupied(network, x, y)) return false

  cell.active = (cell.active + 1) % cell.shapes.length
  rebuildTopology(network)
  network.bumpBlocked = undefined
  return true
}

function isUncoupleAllowed(
  network: Network,
  wagonId: string,
  parentMap: ReadonlyMap<string, string>,
): boolean {
  const { locomotive: loco } = network
  if (!loco) return false
  if (!network.wagons.has(wagonId)) return false

  const parentId = parentMap.get(wagonId)
  if (!parentId) return false

  const detachedWagons = new Set<string>([wagonId])
  const queue = [wagonId]
  for (let i = 0; i < queue.length; i++) {
    const current = getVehicleById(network, queue[i])
    if (!current) continue

    for (const nextId of [current.linkedFront, current.linkedBack]) {
      if (nextId && nextId !== parentId && !detachedWagons.has(nextId)) {
        detachedWagons.add(nextId)
        queue.push(nextId)
      }
    }
  }

  for (const id of detachedWagons) {
    const v = getVehicleById(network, id)
    if (v && isVehicleNearCurveOrSwitch(network, v)) return false
  }

  return true
}

/** Detaches `wagonId` (and everything beyond it) from the locomotive. Refused while moving. */
export function uncoupleWagon(network: Network, wagonId: string): boolean {
  const loco = network.locomotive
  if (!loco || isLocomotiveMoving(loco)) return false

  const parentMap = findConnectionChain(network, loco.id)
  if (!isUncoupleAllowed(network, wagonId, parentMap)) return false

  const wagon = network.wagons.get(wagonId)!
  const parentId = parentMap.get(wagonId)!
  const parentVehicle = getVehicleById(network, parentId)!
  const uncoupledSideOnWagon = wagon.linkedFront === parentId ? 'linkedFront' : 'linkedBack'
  const uncoupledSideOnParent =
    parentVehicle.linkedFront === wagon.id ? 'linkedFront' : 'linkedBack'

  const movementSignMap = findMovementSignsFromLocomotive(network, loco.id)
  const parentMovementSign = movementSignMap.get(parentId)
  if (!parentMovementSign) return false

  const awaySignOnParent = uncoupledSideOnParent === 'linkedFront' ? -1 : 1
  const awaySignOnLoco = awaySignOnParent * parentMovementSign

  unlinkVehicles(network, wagon, parentVehicle)

  const lock = awaySignOnLoco < 0 ? 'forward' : 'backward'
  const awayDirection = lock === 'forward' ? 'backward' : 'forward'
  // Already pinned against a dead end on the away side: a guard would leave no legal move.
  if (network.bumpBlocked === awayDirection) {
    computeUncoupleStatus(network)
    return true
  }

  loco.uncoupleDirectionLock = lock
  loco.lastUncoupledCollisionKey = [
    `${wagonId}:${uncoupledSideOnWagon}:${wagon.direction}`,
    `${parentVehicle.id}:${uncoupledSideOnParent}:${parentVehicle.direction}`,
  ]
    .sort()
    .join('|')

  computeUncoupleStatus(network)

  return true
}

function findMovementSignsFromLocomotive(network: Network, startId: string): Map<string, 1 | -1> {
  const signMap = new Map<string, 1 | -1>()
  const queue = [startId]
  signMap.set(startId, 1)

  while (queue.length > 0) {
    const currentId = queue.shift()!
    const current = getVehicleById(network, currentId)
    const currentSign = signMap.get(currentId)
    if (!current || !currentSign) continue

    const neighbors = [current.linkedFront, current.linkedBack].filter((id): id is string => !!id)

    for (const nextId of neighbors) {
      if (signMap.has(nextId)) continue

      const next = getVehicleById(network, nextId)
      if (!next) continue

      const isCurrentFront = current.linkedFront === next.id
      const isNextFront = next.linkedFront === current.id
      const relationSign: 1 | -1 = isCurrentFront === isNextFront ? -1 : 1

      signMap.set(nextId, (currentSign * relationSign) as 1 | -1)
      queue.push(nextId)
    }
  }

  return signMap
}

function isCellCurveOrSwitch(network: Network, cellId: string): boolean {
  const cell = network.cells.get(cellId)
  if (!cell) return false
  if (cell.type === 'switch') return true

  const activeShape = cell.shapes[cell.active ?? 0]
  if (!activeShape || activeShape.length !== 2) return false
  const a = activeShape[0] as 'N' | 'S' | 'E' | 'W'
  const b = activeShape[1] as 'N' | 'S' | 'E' | 'W'
  return isPerpendicularTurn(a, b)
}

function isTerminalBumperSide(
  network: Network,
  cellId: string,
  x: number,
  y: number,
  dir: 'N' | 'S' | 'E' | 'W',
): boolean {
  const adj = network.adjacency.get(`${cellId}:${dir}`) || []
  if (adj.some(record => record.type === 'external')) return false

  const offset = DIRS[dir]
  return network.cells.get(`${x + offset.dx},${y + offset.dy}`)?.type === 'bumper'
}

function getTerminalBumperCell(network: Network, vehicle: Vehicle): string | null {
  const edge = getEdge(network, vehicle.edgeId)
  if (!edge) return null

  const portA = parsePort(edge.a)
  const portB = parsePort(edge.b)
  const cellA = `${portA.x},${portA.y}`
  const cellB = `${portB.x},${portB.y}`
  const nodeA = network.cells.get(cellA)
  const nodeB = network.cells.get(cellB)

  if (edge.type === 'external') {
    if (nodeA?.type === 'bumper' && nodeB?.type !== 'bumper') return cellB
    if (nodeB?.type === 'bumper' && nodeA?.type !== 'bumper') return cellA
    return null
  }

  if (cellA !== cellB || !nodeA || isCellCurveOrSwitch(network, cellA)) return null

  const bumperAtA = isTerminalBumperSide(network, cellA, portA.x, portA.y, portA.direction)
  const bumperAtB = isTerminalBumperSide(network, cellA, portB.x, portB.y, portB.direction)
  if (!bumperAtA && !bumperAtB) return null

  const spatialU =
    edge.length <= 0
      ? 0
      : vehicle.direction === 1
        ? vehicle.t / edge.length
        : 1 - vehicle.t / edge.length
  if (bumperAtA && spatialU <= 0.5) return cellA
  if (bumperAtB && spatialU >= 0.5) return cellA

  return null
}

function isVehicleNearCurveOrSwitch(network: Network, vehicle: Vehicle): boolean {
  const edge = getEdge(network, vehicle.edgeId)
  if (!edge) return false

  const portA = parsePort(edge.a)
  const portB = parsePort(edge.b)
  const cellA = `${portA.x},${portA.y}`
  const cellB = `${portB.x},${portB.y}`

  if (isCellCurveOrSwitch(network, cellA)) return true
  if (isCellCurveOrSwitch(network, cellB)) return true

  const terminalBumperPocketCell = getTerminalBumperCell(network, vehicle)

  const checkAdjacency = (cellKey: string) => {
    if (cellKey === terminalBumperPocketCell) return false

    for (const dir of ['N', 'S', 'E', 'W']) {
      const portStr = `${cellKey}:${dir}`
      const adjList = network.adjacency.get(portStr) || []
      for (const adj of adjList) {
        if (adj.type === 'external') {
          const p = parsePort(adj.to)
          if (isCellCurveOrSwitch(network, `${p.x},${p.y}`)) return true
        }
      }
    }
    return false
  }

  if (checkAdjacency(cellA)) return true
  if (cellA !== cellB && checkAdjacency(cellB)) return true

  return false
}

function isPerpendicularTurn(from: 'N' | 'S' | 'E' | 'W', to: 'N' | 'S' | 'E' | 'W'): boolean {
  const fromVertical = from === 'N' || from === 'S'
  const toVertical = to === 'N' || to === 'S'
  return fromVertical !== toVertical
}

export function setNPCSpeed(network: Network, npcId: string, speed: number) {
  const npc = network.npcs.get(npcId)
  if (!npc) return

  npc.speed = speed
}
